import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { MVTLayer } from '@deck.gl/geo-layers';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { API_ENDPOINTS as UTILITY_API_ENDPOINTS } from '../../config/utilityApiConfig';
import { geoJsonToGoogleMapsPath, createBoundsFromGeoJson } from '../../utils/geoJsonUtils';
import { setSelectedUtility } from '../../utils/utilitySelection';

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: true,
  fullscreenControl: true,
  styles: [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
  ],
};

function normalizeName(name) {
  if (!name || typeof name !== 'string') return '';
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

function parseTerritoryFeature(feature) {
  if (!feature || typeof feature !== 'object') return null;
  const properties = feature.properties || {};
  return {
    id: feature.id ?? properties.id,
    name: properties.NAME || properties.name || '',
    geometry: feature.geometry || null,
  };
}

function getZctaFromFeature(infoObject) {
  const properties = infoObject?.properties || {};
  return (
    properties.zcta5ce20 ||
    properties.ZCTA5CE20 ||
    properties.geoid20 ||
    properties.GEOID20 ||
    null
  );
}

function hashToSeed(seedString) {
  let h = 2166136261;
  for (let i = 0; i < seedString.length; i += 1) {
    h ^= seedString.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed) {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function buildCurveForZcta(zcta, utilityTerritoryId, startYear, endYear) {
  const seed = hashToSeed(`${utilityTerritoryId}|${zcta}`);
  const rand = mulberry32(seed);
  const totalYears = Math.max(2, endYear - startYear + 1);
  const startAdoption = 1 + rand() * 8;
  const endAdoption = 45 + rand() * 50;
  const growthPower = 1.15 + rand() * 1.75;
  let previous = startAdoption;
  const yearToValue = {};

  for (let offset = 0; offset < totalYears; offset += 1) {
    const year = startYear + offset;
    const t = offset / (totalYears - 1);
    const eased = Math.pow(t, growthPower);
    const jitter = (rand() - 0.5) * 1.5;
    const value = startAdoption + (endAdoption - startAdoption) * eased + jitter;
    previous = clamp(Math.max(previous, value), 0, 100);
    yearToValue[year] = Number(previous.toFixed(2));
  }

  return yearToValue;
}

function adoptionToColor(adoptionPercent, isSelected) {
  const ratio = clamp((adoptionPercent || 0) / 100, 0, 1);
  const from = [224, 242, 254];
  const to = [109, 40, 217];
  const rgb = from.map((v, idx) => Math.round(v + (to[idx] - v) * ratio));
  const alpha = isSelected ? 230 : 200;
  return [...rgb, alpha];
}

function extractZctasFromTiles(tiles) {
  const zctas = new Set();
  const addMaybeZcta = (featureLike) => {
    const zcta = getZctaFromFeature(featureLike);
    if (zcta) zctas.add(String(zcta));
  };

  (tiles || []).forEach((tile) => {
    const content = tile?.content;
    if (!content || typeof content !== 'object') return;

    Object.values(content).forEach((layerValue) => {
      const features =
        Array.isArray(layerValue) ? layerValue : Array.isArray(layerValue?.features) ? layerValue.features : [];
      features.forEach((feature) => addMaybeZcta(feature));
    });
  });

  return [...zctas];
}

function ZctaOverlapAdoptionMap({ selection, height = 420, startYear = 2026, endYear = 2050 }) {
  const navigate = useNavigate();
  const { isLoaded, loadError } = useGoogleMaps();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const infoWindowRef = useRef(null);
  const utilityPolygonsRef = useRef([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [territory, setTerritory] = useState(null);
  const [showZctaLayer, setShowZctaLayer] = useState(true);
  const [showTerritoryLayer, setShowTerritoryLayer] = useState(true);
  const [zctaList, setZctaList] = useState([]);
  const [selectedZctaOption, setSelectedZctaOption] = useState(null);
  const [selectedYear, setSelectedYear] = useState(startYear);
  const [zctaCurves, setZctaCurves] = useState({});
  const [fullscreenElement, setFullscreenElement] = useState(
    typeof document !== 'undefined' ? document.fullscreenElement : null
  );

  const selectionKey = useMemo(
    () =>
      `${selection?.stateCode || ''}|${selection?.utilityName || ''}|${selection?.utilityTerritoryId || ''}`,
    [selection?.stateCode, selection?.utilityName, selection?.utilityTerritoryId]
  );
  const selectedZcta = selectedZctaOption?.value || null;

  useEffect(() => {
    setSelectedYear(clamp(selectedYear, startYear, endYear));
  }, [startYear, endYear]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handler = () => setFullscreenElement(document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  useEffect(() => {
    if (!isLoaded || !mapContainerRef.current || mapRef.current) return;
    mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
      center: { lat: 39.8283, lng: -98.5795 },
      zoom: 5,
      ...mapOptions,
    });
  }, [isLoaded]);

  useEffect(() => {
    const fetchTerritory = async () => {
      if (!selection?.stateCode || !selection?.utilityName) {
        setTerritory(null);
        return;
      }

      const authToken = window.localStorage.getItem('authToken');
      const headers = authToken ? { Authorization: `Token ${authToken}` } : {};
      setLoading(true);
      setError(null);
      try {
        const maybeId = selection?.utilityTerritoryId;
        if (maybeId != null && String(maybeId).trim() !== '') {
          const detailUrl = `${API_ENDPOINTS.getUtilityTerritoryDetail}${maybeId}/`;
          const detailResp = await axios.get(detailUrl, {
            headers,
            params: { geometry: 'true' },
          });
          const parsed = parseTerritoryFeature(detailResp.data);
          if (parsed?.id && parsed?.geometry) {
            setTerritory(parsed);
            return;
          }
        }

        const listResp = await axios.get(API_ENDPOINTS.getUtilityTerritories, {
          headers,
          params: {
            geometry: 'true',
            state: selection.stateCode,
            search: selection.utilityName,
            page_size: 50,
          },
        });

        const territories = Array.isArray(listResp.data?.territories)
          ? listResp.data.territories.map(parseTerritoryFeature).filter(Boolean)
          : [];
        const targetName = normalizeName(selection.utilityName);
        const exact = territories.find((t) => normalizeName(t.name) === targetName);
        const loose = territories.find((t) => normalizeName(t.name).includes(targetName));
        const matched = exact || loose || null;

        if (!matched?.id || !matched?.geometry) {
          setTerritory(null);
          setError('Unable to resolve utility territory for this selection.');
          return;
        }

        setTerritory(matched);
        setSelectedUtility({
          stateCode: selection.stateCode,
          utilityName: selection.utilityName,
          utilityTerritoryId: matched.id,
        });
      } catch (err) {
        setTerritory(null);
        setError(err?.response?.data?.error || 'Failed to load utility territory.');
      } finally {
        setLoading(false);
      }
    };

    fetchTerritory();
  }, [selectionKey]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    utilityPolygonsRef.current.forEach((polygon) => polygon.setMap(null));
    utilityPolygonsRef.current = [];

    if (!territory?.geometry?.coordinates) return;

    const geom = territory.geometry;
    const geomType = geom.type || 'MultiPolygon';
    const paths = geoJsonToGoogleMapsPath(geom.coordinates, geomType);
    const newPolygons = [];

    if (geomType === 'MultiPolygon') {
      paths.forEach((polygonPath) => {
        const polygon = new window.google.maps.Polygon({
          paths: polygonPath,
          strokeColor: '#2563EB',
          strokeOpacity: 1,
          strokeWeight: 3,
          fillColor: '#2563EB',
          fillOpacity: 0.22,
          clickable: false,
          zIndex: 10,
        });
        polygon.setMap(map);
        newPolygons.push(polygon);
      });
    } else {
      const polygon = new window.google.maps.Polygon({
        paths,
        strokeColor: '#2563EB',
        strokeOpacity: 1,
        strokeWeight: 3,
        fillColor: '#2563EB',
        fillOpacity: 0.22,
        clickable: false,
        zIndex: 10,
      });
      polygon.setMap(map);
      newPolygons.push(polygon);
    }
    utilityPolygonsRef.current = newPolygons;
    utilityPolygonsRef.current.forEach((polygon) => polygon.setMap(showTerritoryLayer ? map : null));

    const bounds = createBoundsFromGeoJson(geom.coordinates, geomType);
    if (bounds) map.fitBounds(bounds);
  }, [territory]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    utilityPolygonsRef.current.forEach((polygon) => polygon.setMap(showTerritoryLayer ? map : null));
  }, [showTerritoryLayer]);

  useEffect(() => {
    const fetchZctaList = async () => {
      if (!territory?.id) {
        setZctaList([]);
        setSelectedZctaOption(null);
        setZctaCurves({});
        return;
      }
      try {
        const authToken = window.localStorage.getItem('authToken');
        const headers = authToken ? { Authorization: `Token ${authToken}` } : {};
        const response = await axios.get(API_ENDPOINTS.getUtilityZctaOverlaps, {
          headers,
          params: {
            utility_territory_id: territory.id,
            limit: 1000,
          },
        });
        const items = Array.isArray(response.data?.results) ? response.data.results : [];
        const allZctas = items
          .map((item) => item?.zcta)
          .filter((value) => typeof value === 'string' && value.trim() !== '');
        setZctaList(allZctas);
      } catch (err) {
        // Keep map functional even if API is unavailable; dropdown can still be
        // populated from loaded vector-tile features.
        setZctaList([]);
      }
    };
    fetchZctaList();
  }, [territory?.id]);

  useEffect(() => {
    if (!territory?.id || zctaList.length === 0) {
      setZctaCurves({});
      return;
    }
    const nextCurves = {};
    zctaList.forEach((zcta) => {
      nextCurves[zcta] = buildCurveForZcta(zcta, territory.id, startYear, endYear);
    });
    setZctaCurves(nextCurves);
  }, [territory?.id, zctaList, startYear, endYear]);

  const loadZctaOptions = async (inputValue) => {
    const localFilter = (query) =>
      zctaList
        .filter((zcta) => zcta.includes(query))
        .slice(0, 100)
        .map((zcta) => ({ value: zcta, label: zcta }));

    if (!territory?.id) return localFilter((inputValue || '').trim());
    try {
      const authToken = window.localStorage.getItem('authToken');
      const headers = authToken ? { Authorization: `Token ${authToken}` } : {};
      const response = await axios.get(API_ENDPOINTS.getUtilityZctaOverlaps, {
        headers,
        params: {
          utility_territory_id: territory.id,
          search: inputValue || '',
          limit: 100,
        },
      });
      const items = Array.isArray(response.data?.results) ? response.data.results : [];
      return items
        .map((item) => item?.zcta)
        .filter(Boolean)
        .map((zcta) => ({ value: zcta, label: zcta }));
    } catch (err) {
      return localFilter((inputValue || '').trim());
    }
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!overlayRef.current) {
      overlayRef.current = new GoogleMapsOverlay({
        interleaved: false,
        pickingRadius: 10,
      });
      overlayRef.current.setMap(map);
    }

    if (!territory?.id || !showZctaLayer) {
      overlayRef.current.setProps({ layers: [] });
      return;
    }

    const url = `${UTILITY_API_ENDPOINTS.vectorTiles.utilityOverlapZipCodes}?utility_territory_id=${territory.id}`;
    const overlapLayer = new MVTLayer({
      id: 'utility_overlap_zip_codes',
      data: url,
      loadOptions: { mvt: { layers: ['utility_overlap_zip_codes'] } },
      minZoom: 0,
      maxZoom: 22,
      pickable: true,
      filled: true,
      stroked: true,
      autoHighlight: true,
      highlightColor: [255, 255, 255, 140],
      getFillColor: (f) => {
        const zcta = getZctaFromFeature(f);
        const adoption = zctaCurves?.[zcta]?.[selectedYear] ?? 0;
        return adoptionToColor(adoption, zcta === selectedZcta);
      },
      getLineColor: (f) => {
        const zcta = getZctaFromFeature(f);
        return zcta === selectedZcta ? [30, 64, 175, 255] : [109, 40, 217, 215];
      },
      getLineWidth: (f) => {
        const zcta = getZctaFromFeature(f);
        return zcta === selectedZcta ? 4 : 2;
      },
      updateTriggers: {
        getFillColor: [selectedYear, selectedZcta, zctaCurves],
        getLineColor: [selectedZcta],
        getLineWidth: [selectedZcta],
      },
      lineWidthMinPixels: 1,
      parameters: { depthTest: false },
      onViewportLoad: (tiles) => {
        const discoveredZctas = extractZctasFromTiles(tiles);
        if (!discoveredZctas.length) return;
        setZctaList((prev) => {
          if (!prev.length) return discoveredZctas.sort();
          const merged = new Set(prev);
          discoveredZctas.forEach((item) => merged.add(item));
          return [...merged].sort();
        });
      },
      onClick: (info) => {
        const zcta = getZctaFromFeature(info?.object);
        if (!zcta) return;
        setSelectedZctaOption({ value: zcta, label: zcta });
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
        }
        const clickCoordinate = info?.coordinate || [];
        const [lng, lat] = clickCoordinate;
        const infoWindow = new window.google.maps.InfoWindow({
          position:
            Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : map.getCenter(),
        });
        const wrapper = document.createElement('div');
        wrapper.className = 'p-1';
        const title = document.createElement('div');
        title.className = 'text-sm text-gray-800 mb-2';
        title.textContent = `ZCTA: ${zcta}`;
        const actionBtn = document.createElement('button');
        actionBtn.className =
          'px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded';
        actionBtn.textContent = 'Open trend';
        actionBtn.onclick = () => {
          infoWindow.close();
          navigate(`/utility/workflow/system-models/passenger-ev/adoption-trend/zcta/${zcta}`);
        };
        wrapper.appendChild(title);
        wrapper.appendChild(actionBtn);
        infoWindow.setContent(wrapper);
        infoWindow.open({ map });
        infoWindowRef.current = infoWindow;
      },
    });

    overlayRef.current.setProps({ layers: [overlapLayer] });
  }, [territory?.id, navigate, showZctaLayer, selectedZcta, selectedYear, zctaCurves]);

  useEffect(
    () => () => {
      utilityPolygonsRef.current.forEach((polygon) => polygon.setMap(null));
      utilityPolygonsRef.current = [];
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }
      mapRef.current = null;
    },
    []
  );

  if (loadError) {
    return (
      <div className="w-full rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Failed to load map: {loadError.message}
      </div>
    );
  }

  const mapContainerEl = mapContainerRef.current;
  const isMapFullscreen =
    Boolean(fullscreenElement) && Boolean(mapContainerEl) && mapContainerEl.contains(fullscreenElement);
  // If Google Maps fullscreens a nested element, any UI not inside that fullscreen element
  // can disappear. Portal the legend into the active fullscreen element when applicable.
  const legendPortalTarget = mapContainerEl ? (isMapFullscreen ? fullscreenElement : mapContainerEl) : null;
  // react-select uses a portal for the menu; in fullscreen it must also be a descendant
  // of the fullscreen element or it may not render.
  const selectMenuPortalTarget = isMapFullscreen ? fullscreenElement : document.body;

  return (
    <div className="relative w-full rounded-xl border border-gray-200 overflow-hidden">
      <div ref={mapContainerRef} style={{ height, width: '100%' }} className="relative" />
      {legendPortalTarget &&
        createPortal(
          <div className="absolute right-14 top-4 z-[1000] bg-white/95 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-700 space-y-2 min-w-[220px] pointer-events-auto">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showZctaLayer}
                onChange={(e) => setShowZctaLayer(e.target.checked)}
              />
              <span>Show ZCTA layer</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showTerritoryLayer}
                onChange={(e) => setShowTerritoryLayer(e.target.checked)}
              />
              <span>Show service territory</span>
            </label>
            <div>
              <p className="text-[11px] text-gray-500 mb-1">Filter / highlight ZIP code</p>
              <AsyncSelect
                cacheOptions
                defaultOptions={zctaList.slice(0, 50).map((zcta) => ({ value: zcta, label: zcta }))}
                value={selectedZctaOption}
                loadOptions={loadZctaOptions}
                onChange={setSelectedZctaOption}
                isClearable
                placeholder="Search ZIP code..."
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition="fixed"
                styles={{
                  control: (base) => ({ ...base, minHeight: 30, fontSize: 12 }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, fontSize: 12 }),
                }}
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <span>Year</span>
                <span className="font-semibold">{selectedYear}</span>
              </div>
              <input
                type="range"
                min={startYear}
                max={endYear}
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex items-center justify-between text-[10px] text-gray-500">
                <span>0%</span>
                <span>100%</span>
              </div>
              <div className="h-2 rounded bg-gradient-to-r from-sky-100 to-violet-700" />
            </div>
          </div>,
          legendPortalTarget
        )}
      {(loading || !isLoaded) && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
          <p className="text-sm text-gray-700">Loading ZIP-level adoption map...</p>
        </div>
      )}
      {error && (
        <div className="absolute left-4 top-4 bg-white/95 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 max-w-sm">
          {error}
        </div>
      )}
    </div>
  );
}

export default ZctaOverlapAdoptionMap;
